import { useState, useCallback } from 'react';
import { tokenStorage } from '@/lib/token';
import { formatBytes, type DownloadState } from '@/components/backup/DownloadProgressModal';

interface DownloadInfo {
  state: DownloadState;
  progress: number;
  fileName: string;
  fileSize: string;
  error?: string;
}

export function useBackupDownload() {
  const [downloadInfo, setDownloadInfo] = useState<DownloadInfo>({
    state: 'idle',
    progress: 0,
    fileName: '',
    fileSize: '',
  });
  const [modalOpen, setModalOpen] = useState(false);

  const download = useCallback(async (url: string, fileName: string) => {
    setDownloadInfo({ state: 'downloading', progress: 0, fileName, fileSize: '' });
    setModalOpen(true);

    const token = tokenStorage.getAccessToken();

    try {
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const contentLength = response.headers.get('Content-Length');
      const totalBytes = contentLength ? parseInt(contentLength, 10) : 0;

      if (totalBytes) {
        setDownloadInfo((prev) => ({ ...prev, fileSize: formatBytes(totalBytes) }));
      }

      // If no content-length or no body, fallback to simple blob
      if (!totalBytes || !response.body) {
        const blob = await response.blob();
        triggerDownload(blob, fileName);
        setDownloadInfo((prev) => ({
          ...prev,
          state: 'done',
          progress: 100,
          fileSize: formatBytes(blob.size),
        }));
        return;
      }

      // Stream with progress
      const reader = response.body.getReader();
      const chunks: Uint8Array[] = [];
      let receivedBytes = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        chunks.push(value);
        receivedBytes += value.length;

        const progress = Math.round((receivedBytes / totalBytes) * 100);
        setDownloadInfo((prev) => ({
          ...prev,
          progress,
          fileSize: `${formatBytes(receivedBytes)} / ${formatBytes(totalBytes)}`,
        }));
      }

      const blob = new Blob(chunks as BlobPart[]);
      triggerDownload(blob, fileName);

      setDownloadInfo((prev) => ({
        ...prev,
        state: 'done',
        progress: 100,
        fileSize: formatBytes(blob.size),
      }));
    } catch (err) {
      console.error('Download error:', err);
      setDownloadInfo((prev) => ({
        ...prev,
        state: 'error',
        error: err instanceof Error ? err.message : 'Erreur inconnue',
      }));
    }
  }, []);

  const reset = useCallback(() => {
    setDownloadInfo({ state: 'idle', progress: 0, fileName: '', fileSize: '' });
    setModalOpen(false);
  }, []);

  return { downloadInfo, modalOpen, setModalOpen, download, reset };
}

function triggerDownload(blob: Blob, fileName: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
